// localStorage-backed mock store for Phase 2, keyed per customer so two
// customers on one device never see each other's data (spec §17.13).
// Money is stored in paise (integer) to avoid float drift.
import type {
  Beneficiary,
  Nominee,
  Payment,
  ProfileSettings,
  ServiceRequest,
  StatementEntry,
} from '@/types/phase2.types';
import type { LoansState } from '@/types/loan.types';
import type { Phase4State } from '@/types/phase4.types';

export interface LimitsState {
  /** YYYY-MM-DD (mock clock) of the daily debit totals below. */
  dailyDate: string;
  dailyUsedPaise: number;
  upiUsedPaise: number;
  mpinFailures: number;
  mpinLockedUntil?: number;
  paymentOtpFailures: number;
  paymentsLockedUntil?: number;
  newDeviceUntil?: number;
  debitsFrozen: boolean;
  skipCooling: boolean;
  removeTimeLimits: boolean;
  nextOutcome?: 'SUCCESS' | 'PENDING' | 'FAILED';
  clockOffsetMs: number;
  upiPin?: string;
  upiEnabled: boolean;
  mpin: string;
  password: string;
  mobileBanking: boolean;
}

export interface MockDb {
  seeded: boolean;
  productType: 'SAVINGS' | 'CURRENT' | 'SALARY';
  balancePaise: number;
  beneficiaries: Beneficiary[];
  payments: Payment[];
  approvalTasks: import('@/types/phase2.types').TransactionApprovalTask[];
  ledger: StatementEntry[];
  serviceRequests: ServiceRequest[];
  paymentCounter: number;
  nominees: Nominee[];
  settings: ProfileSettings;
  photoDataUrl?: string;
  kycState: 'FULL' | 'LIMITED' | 'PENDING';
  previousLoginAt?: number;
  currentLoginAt?: number;
  sessionMarked: boolean;
  limits: LimitsState;
  loans: LoansState;
  phase4: Phase4State;
}

const PREFIX = 'nova_phase2_mock_';
const GLOBAL_KEY = 'nova_phase2_mock_global';

export const currentCustomerKey = (): string =>
  localStorage.getItem('customerId') || 'guest';

const keyFor = (customerKey: string): string => `${PREFIX}${customerKey}`;

const defaultLimits = (): LimitsState => ({
  dailyDate: '',
  dailyUsedPaise: 0,
  upiUsedPaise: 0,
  mpinFailures: 0,
  paymentOtpFailures: 0,
  debitsFrozen: false,
  // Dev default: new beneficiaries are payable immediately (the 30-minute
  // cooling can be re-enabled from the dev test panel to test that path).
  skipCooling: true,
  removeTimeLimits: false,
  clockOffsetMs: 0,
  upiEnabled: true,
  mpin: '123456',
  password: 'Password@1',
  mobileBanking: true,
});

const defaultLoans = (): LoansState => ({
  records: [],
  alerts: [],
  failNextEmi: false,
  goldRateRupeesPerGram: 10_500,
  collateralShockPct: 0,
  pledgedAcrossLoansGrams: { ornaments: 0, coins: 0 },
  deregistered: false,
  restricted: false,
  jointHolder: false,
  businessProfile: false,
  lastSalaryRupees: 80_000,
  ageYears: 30,
  monthlyIncomeRupees: 50_000,
  pan: 'ABCDE1234F',
});

// Phase 4 defaults. The first re-KYC record is Low risk from the opening date
// (spec §5.2); the welcome cheque book is seeded by the bank-services handler
// the first time cheques are read (spec §5.1), not here, so the account type
// and opening date are known by then.
const defaultPhase4 = (): Phase4State => ({
  notifications: [],
  ipoBlockedPaise: 0,

  chequeBooks: [],
  positivePay: [],
  rekyc: {
    risk: 'LOW',
    lastUpdatedAt: Date.now(),
    dueAt: Date.now() + 10 * 365 * 24 * 3_600_000,
    remindersSent: [],
  },
  gstRegistrations: [],
  fatca: {
    foreignTaxResident: false,
    countries: [],
    countryOfBirth: 'India',
    citizenship: 'India',
    usPerson: false,
    lastUpdatedAt: Date.now(),
  },
  favourites: [],
  disputes: [],
  fraudReports: [],
  lockerWaitlist: [],
  lockers: [],
  lockerWaitCounter: 14,

  billers: [],
  billPayments: [],
  mandates: [],
  billComplaints: [],
  nextBillFails: false,

  deposits: [],
  mfOnboarded: false,
  mfKra: 'VALIDATED',
  mfNominees: [],
  mfHoldings: [],
  ipoApplications: [],
  sgbHoldings: [],
  sgbWindowOpen: false,

  cardApplications: [],
  cards: [],
  networkChoiceEnabled: false,
});

export const defaultDb = (): MockDb => ({
  seeded: false,
  productType: 'SAVINGS',
  balancePaise: 0,
  beneficiaries: [],
  payments: [],
  approvalTasks: [],
  ledger: [],
  serviceRequests: [],
  paymentCounter: 0,
  nominees: [],
  settings: { smsServices: true, notifications: true, soundNotification: false },
  kycState: 'FULL',
  sessionMarked: false,
  limits: defaultLimits(),
  loans: defaultLoans(),
  phase4: defaultPhase4(),
});

export const loadDb = (customerKey: string = currentCustomerKey()): MockDb => {
  try {
    const raw = localStorage.getItem(keyFor(customerKey));
    if (!raw) return defaultDb();
    const parsed = JSON.parse(raw) as Partial<MockDb>;
    // Merge over defaults so fields added later never come back undefined.
    return {
      ...defaultDb(),
      ...parsed,
      approvalTasks: parsed.approvalTasks ?? [],
      limits: { ...defaultLimits(), ...(parsed.limits ?? {}) },
      loans: { ...defaultLoans(), ...(parsed.loans ?? {}) },
      phase4: { ...defaultPhase4(), ...(parsed.phase4 ?? {}) },
    };
  } catch {
    return defaultDb();
  }
};

export const saveDb = (db: MockDb, customerKey: string = currentCustomerKey()): void => {
  localStorage.setItem(keyFor(customerKey), JSON.stringify(db));
};

/** Clears every Phase 2 mock key for the customer — called on logout (§17.13). */
export const clearMockDb = (customerKey: string = currentCustomerKey()): void => {
  localStorage.removeItem(keyFor(customerKey));
};

/** Global SR counter — references count up across all customers (spec §15). */
export const nextServiceRequestRef = (): string => {
  let next = 1;
  try {
    const raw = localStorage.getItem(GLOBAL_KEY);
    const parsed = raw ? (JSON.parse(raw) as { srCounter?: number }) : {};
    next = (parsed.srCounter ?? 0) + 1;
    localStorage.setItem(GLOBAL_KEY, JSON.stringify({ srCounter: next }));
  } catch {
    // fall through with next = 1
  }
  return `SR${String(next).padStart(6, '0')}`;
};

// ---------- paise helpers ----------

export const toPaise = (rupees: number): number => Math.round(rupees * 100);
export const toRupees = (paise: number): number => paise / 100;
